import * as React from 'react';
import { styled } from '@mui/material/styles';
import { matchPath, useLocation, useParams } from 'react-router';
import { AppBar, IconButton, Toolbar, Typography } from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import clsx from 'clsx';
import { getOnCloseRedirectUrl, setOnCloseRedirectUrl } from 'components/user/localAuth';
import { CONTENT_MAX_WIDTH, NAVBAR_ICON_BUTTON_PADDING } from 'config/constants';
import { useAuth } from 'lib/useAuth';
import { useLocalHistory } from 'lib/useLocalHistory';
import { ROUTES } from 'pages/routes';
import { AppBarUserMenu } from 'lib/appbar/AppBarUserMenu';

const PREFIX = 'ViewBillHeader';

const classes = {
    appBar: `${PREFIX}appBar`,
    title: `${PREFIX}title`,
    backButton: `${PREFIX}backButton`,
    marginBlock: `${PREFIX}marginBlock`,
    emptyButton: `${PREFIX}emptyButton`,
    icon: `${PREFIX}icon`,
    userAvatar: `${PREFIX}userAvatar`,
    userInitials: `${PREFIX}userInitials`,
    toolbar: `${PREFIX}toolbar`
};

const Root = styled('div')(({ theme }) => ({
    [`& .${classes.appBar}`]: {
        backgroundColor: theme.palette.primary.contrastText,
        color: theme.palette.primary.main
    },

    [`& .${classes.title}`]: {
        textAlign: 'center',
        width: `calc(100% - ${theme.spacing(12)})`,
        fontFamily: `TenantFont, ${theme.typography.fontFamily}`,
        fontWeight: 'bold',
        color: theme.palette.titleTextColour
    },

    [`& .${classes.backButton}`]: {
        color: theme.palette.primary.main
    },

    [`& .${classes.marginBlock}`]: {
        height: '48px',
        flexShrink: 0,
        width: '100%'
    },

    [`& .${classes.emptyButton}`]: {
        width: '48px',
        height: 1
    },

    [`& .${classes.icon}`]: {
        marginRight: theme.spacing(2)
    },

    [`& .${classes.userAvatar}`]: {
        width: '24px',
        height: '24px',
        borderRadius: '50%'
    },

    [`& .${classes.userInitials}`]: {
        fontSize: theme.spacing(1.5),
        fontWeight: theme.typography.fontWeightBold,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },

    [`& .${classes.toolbar}`]: {
        width: '100%',
        margin: '0 auto',
        maxWidth: `${CONTENT_MAX_WIDTH + NAVBAR_ICON_BUTTON_PADDING}px`
    }
}));

interface IProps {
    title: string;
    disableBack?: boolean;
    disabledMenu?: boolean;
    onBack?: () => void;
    onAuthActionClick?: () => Promise<void>;
    elevation?: number;
}

export const ViewBillHeader: React.FC<React.PropsWithChildren<IProps>> = ({
    onBack,
    onAuthActionClick,
    disableBack,
    disabledMenu,
    title
}) => {
    const { merchantId } = useParams();

    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | undefined>(undefined);
    const [isAvatarAvailable, setIsAvatarAvailable] = React.useState<boolean>(true);
    const { push, navigate } = useLocalHistory();
    const { pathname } = useLocation();
    const { isLoggedIn, user } = useAuth();
    const isAuthorizationUrl = React.useMemo(() => {
        const authorizationRoutes = [
            ROUTES.USER.LOGIN,
            ROUTES.USER.REGISTER,
            ROUTES.USER.DETAILS,
            ROUTES.USER.REWARDS,
            ROUTES.USER.ACTIVITY,
            ROUTES.USER.PAYMENT_METHODS
        ];
        return authorizationRoutes.some(item => !!matchPath(pathname, item));
    }, [pathname]);
    const handleLeftClick = React.useCallback(() => {
        if (typeof onBack === 'function') {
            onBack();
        } else if (isAuthorizationUrl) {
            if (merchantId) {
                const redirectUrl = getOnCloseRedirectUrl(merchantId);
                if (redirectUrl) {
                    push(redirectUrl);
                    setOnCloseRedirectUrl('', merchantId);
                } else {
                    push(ROUTES.BASE, { merchantId });
                }
            }
        } else {
            navigate(-1);
        }
    }, [onBack, isAuthorizationUrl, merchantId, push, navigate]);
    const handleRightClick = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
        setAnchorEl((value?: HTMLElement) => (!!value ? undefined : e.currentTarget));
    }, []);
    const closeMenu = React.useCallback(() => setAnchorEl(undefined), []);

    const menuId = 'app-bar-menu-id';
    const handleError = React.useCallback(() => {
        setIsAvatarAvailable(false);
    }, []);
    const userInitials = React.useMemo(() => user && user.firstName[0] + user.lastName[0], [user]);
    return (
        <Root>
            <div className={classes.marginBlock} />
            <AppBar position="fixed" elevation={0} className={classes.appBar}>
                <Toolbar className={classes.toolbar} disableGutters variant="dense">
                    {!disableBack ? (
                        <IconButton onClick={handleLeftClick} size="large">
                            <ArrowBackIcon className={classes.backButton} />
                        </IconButton>
                    ) : (
                        <div className={classes.emptyButton} />
                    )}
                    <Typography variant="h6" className={classes.title}>
                        {title}
                    </Typography>
                    {!disabledMenu && (
                        <IconButton
                            aria-label="show more"
                            aria-controls={menuId}
                            aria-haspopup="true"
                            onClick={handleRightClick}
                            color="inherit"
                            size="large"
                        >
                            {isLoggedIn &&
                                (isAvatarAvailable ? (
                                    <img
                                        className={classes.userAvatar}
                                        src={`${process.env.MEDIA_URL}/users/avatars/${user?.id}.jpg`}
                                        onError={handleError}
                                    />
                                ) : (
                                    <span className={clsx(classes.userAvatar, classes.userInitials)}>
                                        {userInitials}
                                    </span>
                                ))}
                            {!isLoggedIn && <AccountCircle />}
                        </IconButton>
                    )}
                </Toolbar>
            </AppBar>
            {!!anchorEl && !disabledMenu && (
                <AppBarUserMenu
                    anchorEl={anchorEl}
                    menuId={menuId}
                    onClose={closeMenu}
                    onAuthActionClick={onAuthActionClick}
                />
            )}
        </Root>
    );
};
